import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { registerSqlDatasets } from 'tio-reporting/utils/dataset-utils';
import { sqlDatasetQuery } from 'tio-data/requests/sql-dataset';
import type SessionContextService from 'tio-employee/services/session-context';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import type { SqlDataset } from 'tio-data/data/resources/sql-dataset.type';

const ALLOWED_ROLES = ['TIO_ADMIN', 'EMPLOYEE_ADMIN', 'ACCOUNT_OWNER'];

export default class AuthenticatedAdminReportingRoute extends Route {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  async beforeModel() {
    if (!this.sessionContext.isLoaded) {
      try {
        await this.sessionContext.load();
      } catch (e) {
        console.error('Could not load session context:', e);
      }
    }

    const hasAccess = this.sessionContext.roles.some((role) =>
      ALLOWED_ROLES.includes(role.relationshipType)
    );

    if (!hasAccess) {
      this.router.transitionTo('authenticated.dashboard');
      return;
    }

    // Register published SQL-backed datasets (RFC 062) so saved SQL-dataset
    // reports resolve in the report viewer — without this, isSqlDataset()
    // is false and the slug would be sent to /api/v2/search as a model
    // type. The backend scopes the list to datasets this user may query.
    // Non-fatal on failure: OpenSearch reporting works regardless.
    try {
      const { content } = (await this.store.request(sqlDatasetQuery())) as {
        content: { data: SqlDataset[] };
      };
      registerSqlDatasets(
        content.data
          .filter((dataset) => dataset.status === 'published')
          .map((dataset) => ({
            slug: dataset.slug,
            label: dataset.label,
            product: dataset.product,
            crossCompanyCapable: dataset.crossCompanyCapable,
          }))
      );
    } catch (error) {
      // Keep the last-known-good registry: clearing here would reroute
      // already-open SQL-backed reports to /api/v2/search mid-session on a
      // transient failure. First load starts empty by default anyway.
      console.error('Failed to load SQL datasets for reporting:', error);
    }
  }
}
